import { Injectable } from '@angular/core';

import { TranslateService } from '@itonics/ngx-translate-simple';

import { LogService } from '@innovation-os-ng/core/logger';
import { KeyEventData, TimelineStep } from '@innovation-os-ng/ui/chart-ng';
import { CompoundFilter, LeafFilter, RangeReferencePoint, RangeReferenceUnit, RangeUtil } from '@itonics/filters';

import { getTimeQuery } from '../../shared/helpers/filter-helper';
import { calculateTimeSteps, timestampToDateString } from '../../shared/helpers/time-helper';

import {
    DocumentsQueryAggregation,
    ExecutionDetailAsync,
    NormalizedDataResponse,
    SignalsAggregateTimeSeries,
    SignalsDictionary,
    SignalsFilters,
    SignalsType,
    TimeRange
} from '../../shared/model/types';

import { ToastNotificationsService, ToastType } from '@innovation-os-ng/core/toast-notifications';
import { ReplaySubject, Subject } from 'rxjs';
import { normalize } from '../../shared/helpers/normalize-helper';
import { ASYNC_METRIC_OPTIONS, MetricOptions } from '../../shared/model/metric';
import { ApiService } from '../../shared/modules/shared-services/services/api.service';
import { MetricService } from '../../shared/modules/shared-services/services/metric.service';
import { PropertiesService } from '../../shared/modules/shared-services/services/properties.service';
import { KeyEventPostData, NormalizedTimelinePostData } from '../models/keyEvents.model';
import { DateRangeInfoModel, TimelineModel, TimelineParsedResponseData } from '../models/model';

const TIME_SERIES_PROPERTY = 'publishedDate';
enum TimeMetricAggregation {
  MIN = 'min',
  MAX = 'max'
}

/**
 * Default minimum time frame for timeline data.
 */
const DEFAULT_MIN_PUBLISHED_YEAR = 2021;

@Injectable({
  providedIn: 'root'
})
export class TimeSeriesService {
  constructor(
    private readonly apiService: ApiService,
    private readonly logger: LogService,
    private readonly propertiesService: PropertiesService,
    private readonly toastNotificationsService: ToastNotificationsService,
    private readonly translate: TranslateService,
    private readonly metricService: MetricService
  ) {}

  private cachedNormalizedTimelineData: Subject<NormalizedDataResponse> = new ReplaySubject<NormalizedDataResponse>(1);
  private cachedKeyEventsGrowthData: Subject<Array<KeyEventData>> = new ReplaySubject<Array<KeyEventData>>(1);
  public readonly cachedNormalizedTimelineDataObs = this.cachedNormalizedTimelineData.asObservable();
  public readonly cachedKeyEventsGrowthDataObs = this.cachedKeyEventsGrowthData.asObservable();

  /**
   *
   * @param type type of the aggregations e.g. country, doc Type
   * @param query filter
   * @param timeRanges time frame requested
   * @param id If provided will replace all pending previous calls with the same ID
   * @returns Promise Array<DocumentsQueryAggregation>
   */
  public async getTimeSeries(
    type: string,
    query: CompoundFilter,
    timeRanges: Array<number>,
    metricOptions: MetricOptions,
    id?: string
  ): Promise<Array<DocumentsQueryAggregation>> {
    const filter = query.clone();
    this.removeTimeQuery(filter);

    // Certain metric options should be requested asynchronously because calculating
    // their results will otherwise cause a synchronous request to timeout
    const async = ASYNC_METRIC_OPTIONS.indexOf(metricOptions?.metric) !== -1;

    const postBody = {
      aggregations: [
        {
          property: TIME_SERIES_PROPERTY,
          timeRanges,
          childAggregations: [
            {
              property: type,
              ...metricOptions
            }
          ],
          ...metricOptions
        }
      ],
      query: filter.toJson(true),
      limit: 0,
      executionDetails: async ? { asynchronous: ExecutionDetailAsync.Aggregations } : null
    };

    return this.apiService.postDocumentsQuery(postBody, id).then((res) => {
      if (!res?.aggregation || !res.aggregation[TIME_SERIES_PROPERTY]) {
        this.logger.info(`/insights-search/insights-search endpoint is not returning results for ${JSON.stringify(postBody)}`);
        return [];
      }
      return res.aggregation[TIME_SERIES_PROPERTY];
    });
  }

  /**
   * resolve data for timeline
   *
   * @param type type of the aggregations e.g. country, doc Type
   * @param query filter
   * @returns returns a promise with timeline config and data
   */
  public getTimelineAggregations(type: string, query: CompoundFilter): Promise<TimelineModel> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const dateRangeInfo = await this.getDateRanges(query);
      const timeRanges = dateRangeInfo?.dates;
      const timeFrame = dateRangeInfo?.timeFrame;
      const stepsType = dateRangeInfo?.type;

      if (!timeRanges) {
        this.logger.error('Unable to get Time Ranges from filters');

        reject();
        return;
      }

      // Get the metric
      const metricOptions: MetricOptions = this.metricService.getGlobalMetric();

      // Get timeseries data from backend.
      const data = await this.getTimeSeries(type, query, timeRanges, metricOptions, 'timeline-v2');

      // Parse the data and get the Array<SignalsAggregateTimeSeries>
      const parsedTimelineData: TimelineModel = this.parseTimelineData(type, data, metricOptions, stepsType, timeFrame);

      // Resolve the parsed data.
      resolve(parsedTimelineData);
    });
  }

  /**
   * We parse the data coming from backend and prepare
   * data that can be plot in the timeline.
   *
   * @param type - type of the aggregations e.g. country, doc Type
   * @param data - time series data
   * @param metricOptions - Metric option for data.
   * @param stepsType - Time range of timeline data.
   * @param timeFrame - Time frame for timeline.
   *
   * @returns - Returns the Parsed Timeline model.
   */
  private parseTimelineData(
    type: string,
    data: Array<DocumentsQueryAggregation>,
    metricOptions: MetricOptions,
    stepsType: TimelineStep,
    timeFrame: Array<number>
  ): TimelineModel {
    // Get dictionary for the actual type, e.g. country
    const dictionary = this.propertiesService.getPropertyDictionary(type as SignalsType);

    // We remove the first interval that is from 0 to first set of data
    data.shift();

    /*

      Extract categories list

      we need a list of all the existing categories in the data with the total sum of values
      to be able to generate a ranking and to add missing series
      (we can have missing series from BE if there are zero results)
    */
    const categoriesList = this.getCategoriesWithTotalValues(data, type);
    const rankingMap = this.getRankingMap(categoriesList);

    /*

      Build Timeline data
    */
    const timelineData: Array<SignalsAggregateTimeSeries> = data.reduce((collection, series) => {
      const date = timestampToDateString((series.key as TimeRange).to);

      /*
        The data may have missing series for categories that have zero results,
        but for Timeline we need data per each category and per each date-range
        even thought the value is zero, otherwise we will have gaps in the lines
      */
      const aggregations = this.addMissingSeries(categoriesList, series.childAggregations[type]);

      // We use the global maximum value to normalise
      const maxValue = Math.max(...categoriesList.map((d) => d.maxValue));

      return collection.concat(
        aggregations.map((d) => {
          let normalizeFnc;
          if (this.metricService.isMetricLikeRelevance(metricOptions.metric)) {
            // we normalize by the highest value of the entire aggregation
            normalizeFnc = (value) => normalize(0, maxValue, value);
          }

          return this.buildTimelineData(date, d, rankingMap, normalizeFnc, dictionary);
        })
      );
    }, []);

    return {
      // What type of steps do we have? week, month, day...
      type: stepsType,
      // in the range for timeline we want to include the last day in UTC
      range: [`${timestampToDateString(timeFrame[0])}T00:00:00Z`, `${timestampToDateString(timeFrame[1])}T23:59:59Z`],
      dates: timelineData
    };
  }

  /**
   * Fetch the key events growth data.
   *
   * @param postData - Post data to fetch normalized timeline data
   *
   * @returns - Returns the Promise with Array of Key events.
   */
  public async getKeyEventsGrowthData(postData: KeyEventPostData): Promise<Array<KeyEventData>> {
    const keyEventsGrowthData = await this.apiService.getKeyEventsGrowthData(postData).catch((error) => {
      this.logger.info(error);
      return [];
    });
    this.cachedKeyEventsGrowthData.next(keyEventsGrowthData);
    return keyEventsGrowthData;
  }

  /**
   * Fetch the normalized timeline data
   * when key events growth is enabled.
   *
   * @param postData - Post data to fetch normalized timeline data
   * @param query - Filters
   * @param type - property type
   *
   * @returns - Returns the parsed timeline data.
   */
  public async getNormalizedTimelineData(
    postData: NormalizedTimelinePostData,
    query: CompoundFilter,
    type: string
  ): Promise<TimelineParsedResponseData> {
    const dateRangeInfo: DateRangeInfoModel = await this.getDateRanges(query);
    const timeFrame: Array<number> = dateRangeInfo?.timeFrame;
    const timeRanges: Array<number> = dateRangeInfo?.dates;
    const stepsType: TimelineStep = dateRangeInfo?.type;

    if (timeRanges?.length) {
      postData.timeRanges = timeRanges;
    }

    // Add steps to the post request data
    postData.step = stepsType;

    const response: NormalizedDataResponse = await this.apiService.getNormalizedTimelineData(postData);

    this.cachedNormalizedTimelineData.next(response);

    const data: Array<DocumentsQueryAggregation> = response.aggregation[TIME_SERIES_PROPERTY];

    // Get the metric
    const metricOptions: MetricOptions = this.metricService.getGlobalMetric();

    // Parsed Timeline data.
    const parsedData: TimelineModel = this.parseTimelineData(type, data, metricOptions, stepsType, timeFrame);

    // Return the parsed data.
    return {
      response,
      timelineData: parsedData
    };
  }

  private computeTimeQuery(filter: CompoundFilter, toPresent: boolean = false): Promise<LeafFilter> {
    return new Promise((resolve) => {
      const timeFilter: LeafFilter = getTimeQuery(filter, SignalsFilters.SELECTION);
      if (timeFilter) {
        resolve(timeFilter);
        return;
      }
      const filterRef = this.addTimeConstrainToFilter(filter.getQuery(SignalsFilters.SELECTION) as CompoundFilter);
      this.apiService
        .postDocumentsQuery({
          aggregations: [
            { property: SignalsType.PUBLISHED_DATE, metricsAggregations: [TimeMetricAggregation.MAX, TimeMetricAggregation.MIN] }
          ],
          limit: 1,
          query: filterRef.toJson()
        })
        .then(
          (data) => {
            if (data.aggregation.publishedDate) {
              const leafFilter = new LeafFilter(SignalsType.PUBLISHED_DATE);

              // End range at present date or at the last date in the published data
              const rangeMax = toPresent
                ? Date.now()
                : data.aggregation.publishedDate.find((date) => date.key === TimeMetricAggregation.MAX).value;

              leafFilter.setRange({
                gt: {
                  value:
                    data.aggregation.publishedDate.find((date) => date.key === TimeMetricAggregation.MIN).value ??
                    new Date(DEFAULT_MIN_PUBLISHED_YEAR, 1, 1).getTime()
                },
                lt: { value: rangeMax }
              });
              resolve(leafFilter);
            }
          },
          () => {
            this.toastNotificationsService.showToast({
              content: this.translate.instant('signals-ng-visualization-tab.common.error.date_load_failed'),
              duration: 5000,
              type: ToastType.ERROR
            });
          }
        );
    });
  }

  /**
   * Currently we have some faulty dates from Zenodo, which shows a few published dates in the future.
   * So we need to add time constrain
   * It adds the filter which specifies published date should be lesser than current date
   */
  private addTimeConstrainToFilter(filter: CompoundFilter): CompoundFilter {
    const filterRef: CompoundFilter = filter.clone();
    const dateRangeFilter = new LeafFilter(SignalsType.PUBLISHED_DATE);
    const lessThanFilter = {
      reference: RangeReferencePoint.DATE_NOW,
      value: 0,
      unit: RangeReferenceUnit.DAYS
    };

    // Only consider the documents that were published after the year [DEFAULT_MIN_PUBLISHED_YEAR]
    // This adds a greater than constraint to ignore those documents
    const greaterThanFilter = {
      value: new Date(DEFAULT_MIN_PUBLISHED_YEAR, 1, 1).getTime() - 1 // decrease 1 millis
    };

    dateRangeFilter.setRange({
      lt: lessThanFilter,
      gt: greaterThanFilter
    });
    filterRef.addFilter(dateRangeFilter);
    return filterRef;
  }

  /*

    UTILS
   */

  private removeTimeQuery(filter: CompoundFilter): void {
    const selectionTimeFilter = getTimeQuery(filter, SignalsFilters.SELECTION);

    if (selectionTimeFilter) {
      (filter.getQuery(SignalsFilters.SELECTION) as CompoundFilter).removeFilter(selectionTimeFilter);
    }
  }

  /**
   *
   * @param filter query used to extract the date range
   * @returns dates (a list of ranges), timeFrame (start and end) and steps: could be days, months, etc...
   */
  private async getDateRanges(filter: CompoundFilter): Promise<DateRangeInfoModel> {
    /*
      In order to get the time range we need to extract it from the filter
    */
    const timeFilter = await this.computeTimeQuery(filter, true);

    /**
     * TIME RANGES
     * in the range we want to end with the end of the day in milliseconds in order to have data if the user selects only one day
     */
    const queryRange = [
      RangeUtil.convert(timeFilter.getRange().gt),
      timeFilter.getRange().lt ? RangeUtil.convert(timeFilter.getRange().lt) : Date.now()
    ];

    return {
      ...calculateTimeSteps(queryRange[0], queryRange[1]),
      timeFrame: queryRange
    };
  }

  /**
   *
   * @param data aggregations
   * @param type type of the aggregations e.g. country
   * @returns map with key, sum and the max value per category
   */
  private getCategoriesWithTotalValues(
    data: Array<DocumentsQueryAggregation>,
    type: string
  ): Array<{
    sum: number;
    key: string;
    maxValue: number;
  }> {
    const categoriesMap = data.reduce((map, d) => {
      const categoriesPerDate = d.childAggregations[type];

      // We sum all the values per category
      // also we get
      categoriesPerDate.forEach((cpd) => {
        const category = map[cpd.key as string];

        const sum = (category?.sum || 0) + cpd.value;

        const prevMaxValue = category?.maxValue || 0;
        const maxValue = cpd.value > prevMaxValue ? cpd.value : prevMaxValue;

        map[cpd.key as string] = {
          key: cpd.key,
          sum,
          maxValue
        };
      });

      return map;
    }, {});

    return Object.keys(categoriesMap).map((d) => categoriesMap[d]);
  }

  /**
   *
   * @param categoriesList a list with all the categories in the data with the total of documents
   * @returns map with numbers from 1 that represent rankings
   */
  private getRankingMap(
    categoriesList: Array<{
      sum: number;
      key: string;
    }>
  ): { [key: string]: number } {
    return categoriesList
      .sort((a, b) => b.sum - a.sum)
      .reduce((map, d, i) => {
        map[d.key] = i + 1;
        return map;
      }, {});
  }

  /**
   *
   * @param categoriesList a list with all the categories in the data with the total of documents
   * @param aggregations a list of series
   * @returns a list of aggregations
   */
  private addMissingSeries(
    categoriesList: Array<{
      sum: number;
      key: string;
    }>,
    aggregations: Array<DocumentsQueryAggregation>
  ): Array<DocumentsQueryAggregation> {
    if (aggregations.length === categoriesList.length) {
      return aggregations;
    }

    // If there are some missing aggregations we
    // fill the gaps
    const missingAggregations = categoriesList
      .filter((cat) => aggregations.findIndex((d) => d.key === cat.key) === -1)
      .map((d) => ({
        key: d.key,
        value: 0,
        doc_count: 0
      }));
    return aggregations.concat(missingAggregations);
  }

  /**
   *
   * @param date the "to" from range date
   * @param item the aggregation
   * @param rankingMap a map with categories ranked
   * @param dictionary dictionary of the type requested
   * @returns formatted aggregation for timeline
   */
  private buildTimelineData(
    date: string,
    item: DocumentsQueryAggregation,
    rankingMap: { [key: string]: number },
    normalizeFnc: (value: number) => number,
    dictionary?: SignalsDictionary
  ): SignalsAggregateTimeSeries {
    const key = item.key as string;

    return {
      date,
      value: normalizeFnc ? normalizeFnc(item.value) : item.value,
      category: key,
      ranking: rankingMap[key],
      label: dictionary ? dictionary[key] : key
    };
  }
}
